import * as React from "react";
import type { Entry } from "@/shared/types/entries";
import {
	ResponsiveDialog,
	ResponsiveDialogContent,
} from "@/v2/components/ui/responsive-dialog";
import { TooltipButton } from "@/v2/components/ui/tooltip-button";
import { ActionForm, type ActionFormSubmit } from "./action-dialog/ActionForm";
import { MainSourcePicker } from "./action-dialog/MainSourcePicker";

export interface CollapseDialogSubmit extends ActionFormSubmit {
	mainSourceId: number;
}

export interface CollapseDialogProps {
	entries: Entry[];
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSubmit: (values: CollapseDialogSubmit) => void;
}

export function CollapseDialog({
	entries,
	open,
	onOpenChange,
	onSubmit,
}: CollapseDialogProps) {
	const formId = "collapse-entries-form";

	// Default to the longest entry as the anchor — usually the most substantive
	// narrative. Reset whenever the dialog opens with a different selection.
	const defaultMainId = React.useMemo(() => {
		if (entries.length === 0) return undefined;
		const sorted = [...entries].sort(
			(a, b) =>
				(b.units_seconds ?? Math.round((b.duration ?? 0) * 3600)) -
				(a.units_seconds ?? Math.round((a.duration ?? 0) * 3600)),
		);
		return sorted[0].id;
	}, [entries]);

	const [mainSourceId, setMainSourceId] = React.useState<number | undefined>(
		defaultMainId,
	);

	// Re-anchor when the dialog opens with a fresh selection
	React.useEffect(() => {
		if (open) setMainSourceId(defaultMainId);
	}, [open, defaultMainId]);

	return (
		<ResponsiveDialog open={open} onOpenChange={onOpenChange}>
			<ResponsiveDialogContent
				title="Collapse"
				// Wider than the default `max-w-lg` so the side-by-side layout
				// (picker | form) has breathing room at sm+. Mobile stack uses
				// its own dialog code path and ignores this.
				className="sm:max-w-4xl"
				// Make the body itself the flex container — picker and form
				// are direct flex children, so they stretch to the body's
				// height via the default `align-items: stretch` instead of
				// relying on `h-full`, which doesn't reliably propagate
				// through a flex item with `flex-1` as the height source.
				// Mobile (default `flex-col`) lets the body scroll naturally;
				// desktop (`sm:flex-row`) uses `sm:overflow-hidden` and the
				// children scroll independently. Popovers (calendar,
				// comboboxes) use Portal so clipping is moot.
				bodyClassName="flex-1 min-h-0 flex flex-col sm:flex-row overflow-y-auto sm:overflow-hidden"
				footer={
					<div className="flex justify-end items-center gap-2">
						<TooltipButton
							variant="outline"
							size="sm"
							onClick={() => onOpenChange(false)}
							tooltip="Cancel"
						>
							Cancel
						</TooltipButton>
						<TooltipButton
							type="submit"
							form={formId}
							size="sm"
							disabled={mainSourceId === undefined}
							tooltip="Collapse entries"
						>
							Collapse entries
						</TooltipButton>
					</div>
				}
			>
				{entries.length > 0 && mainSourceId !== undefined && (
					<>
						{/* Left: main-source picker. `sm:min-h-0` lets the
						   flex child shrink so its own overflow-y-auto can
						   trigger instead of being capped at content height. */}
						<div className="sm:w-96 sm:shrink-0 sm:border-r border-border p-4 sm:min-h-0 sm:overflow-y-auto">
							<MainSourcePicker
								entries={entries}
								value={mainSourceId}
								onChange={setMainSourceId}
							/>
						</div>
						{/* Right: shared action form. Scrolls its own column on
						   desktop; popovers (calendar, comboboxes) are
						   portaled so clipping is fine. */}
						<div className="flex-1 min-w-0 sm:min-h-0 sm:overflow-y-auto">
							<ActionForm
								formId={formId}
								entries={entries}
								mainSourceId={mainSourceId}
								showUnitsAggregation
								onSubmit={(values) => onSubmit({ ...values, mainSourceId })}
							/>
						</div>
					</>
				)}
			</ResponsiveDialogContent>
		</ResponsiveDialog>
	);
}
